import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRecipe } from '../../contexts/RecipeContext';
import { FaTimes, FaCopy, FaShareAlt, FaTrash, FaLink, FaUsers } from 'react-icons/fa';
import logger from '../../utils/logger';
import '../../styles/recipes/ShoppingListShareModal.css';

/**
 * ShoppingListShareModal - Modal für das Teilen von Einkaufszetteln
 *
 * Features:
 * - Share-Link erstellen
 * - Link kopieren
 * - Native Share API
 * - Aktive Shares verwalten
 *
 * Jeder mit dem Link + angemeldet kann die Liste bearbeiten
 */
const ShoppingListShareModal = ({ isOpen, onClose, listId, listName }) => {
  const { t } = useLanguage();
  const {
    shareShoppingList,
    getActiveShares,
    revokeShoppingListShare,
    removeCollaborator,
    shoppingLists,
    fetchShoppingLists
  } = useRecipe();

  const [shareUrl, setShareUrl] = useState('');
  const [activeShares, setActiveShares] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showCollaboratorsList, setShowCollaboratorsList] = useState(false);

  // Get current list data
  const currentList = shoppingLists.find(l => String(l.id) === String(listId));
  const isOwner = currentList?.isOwner !== false;

  // Debug: Log list info when modal opens
  useEffect(() => {
    if (isOpen) {
      logger.debug('[ShareModal] Modal opened with:', {
        listId,
        listIdType: typeof listId,
        currentList: currentList ? { id: currentList.id, name: currentList.name, isOwner: currentList.isOwner } : null,
        allListIds: shoppingLists.map(l => ({ id: l.id, name: l.name }))
      });
    }
  }, [isOpen, listId]);

  // Reset state when modal closes, load fresh data when it opens
  useEffect(() => {
    if (isOpen && listId && isOwner) {
      // Refresh shopping lists to get latest collaborators
      fetchShoppingLists();
      // Always load fresh data when modal opens
      loadActiveShares();
    } else if (!isOpen) {
      // Reset state when modal closes so next open gets fresh data
      setShareUrl('');
      setActiveShares([]);
      setError(null);
      setSuccessMessage(null);
      setCopied(false);
    }
  }, [isOpen, listId, isOwner]);

  // Build share URL from token
  const buildShareUrl = (token) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/share/shopping/${token}`;
  };

  // Check if a share link is expired
  const isShareExpired = (share) => {
    if (!share.expiresAt) return false;
    return new Date(share.expiresAt) < new Date();
  };

  const loadActiveShares = async (skipUrlUpdate = false) => {
    setIsLoading(true);
    try {
      const shares = await getActiveShares(listId);
      setActiveShares(shares || []);

      // If there's an active share and we haven't just created one, use the newest
      if (shares && shares.length > 0 && !skipUrlUpdate && !shareUrl) {
        // Use the most recent share (last in array or sort by createdAt)
        const newestShare = shares.reduce((newest, current) => {
          if (!newest.createdAt) return current;
          if (!current.createdAt) return newest;
          return new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest;
        }, shares[0]);
        const existingUrl = buildShareUrl(newestShare.shareToken);
        logger.debug('Using existing share:', existingUrl);
        setShareUrl(existingUrl);
      }
    } catch (err) {
      logger.error('Error loading shares:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new share
  const handleCreateShare = async () => {
    // Validate listId before attempting to create share
    if (!listId) {
      setError(t('share.noListSelected') || 'Keine Liste ausgewählt. Bitte warte bis die Liste geladen ist.');
      logger.error('Cannot create share: listId is undefined');
      return;
    }

    setIsCreatingShare(true);
    setError(null);
    try {
      logger.debug('Creating share for list:', listId);
      const url = await shareShoppingList(listId, {
        expiryHours: 24,
        maxUses: 0
      });
      logger.debug('Share created successfully:', url);
      setShareUrl(url);
      await loadActiveShares(true); // Skip URL update since we just set it

      // Auto-copy to clipboard for better UX
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setSuccessMessage(t('share.linkCreatedAndCopied') || 'Link erstellt und kopiert!');
        setTimeout(() => {
          setCopied(false);
          setSuccessMessage(null);
        }, 3000);
      } catch (clipboardErr) {
        logger.debug('Auto-copy failed, user can manually copy:', clipboardErr);
        setSuccessMessage(t('share.linkCreated') || 'Link erstellt!');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      const errorMessage = err?.message || err?.toString() || 'Unknown error';
      // Show actual error message for debugging
      setError(`Fehler: ${errorMessage}`);
      logger.error('Error creating share:', err);
      logger.error('listId was:', listId, 'type:', typeof listId);
    } finally {
      setIsCreatingShare(false);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Error copying to clipboard:', err);
    }
  };

  // Native share
  const handleNativeShare = async () => {
    if (!shareUrl || !navigator.share) return;
    try {
      await navigator.share({
        title: listName || t('share.defaultTitle'),
        text: t('share.inviteText') || 'Hier ist mein Einkaufszettel',
        url: shareUrl
      });
    } catch (err) {
      // User cancelled or error
      if (err.name !== 'AbortError') {
        logger.error('Error sharing:', err);
      }
    }
  };

  // Revoke/delete a share link
  // If expired=true, just delete the link without removing collaborators
  const handleRevokeShare = async (token, isExpired = false) => {
    const collaboratorCount = currentList?.collaborators?.length || 0;

    // Only show warning and remove collaborators for active (non-expired) links
    if (!isExpired && collaboratorCount > 0) {
      const confirmMessage = t('share.revokeConfirmWithCollaborators') ||
        `Wenn du den Link löschst, verlieren alle ${collaboratorCount} Mitbearbeiter den Zugriff auf diese Liste. Fortfahren?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      try {
        // Remove all collaborators so they lose access immediately
        logger.debug(`Removing ${collaboratorCount} collaborators before revoking share`);
        const removePromises = currentList.collaborators.map(collab =>
          removeCollaborator(listId, collab.id).catch(err => {
            logger.warn(`Failed to remove collaborator ${collab.id}:`, err);
          })
        );
        await Promise.all(removePromises);
        logger.debug('All collaborators removed');
      } catch (err) {
        logger.error('Error removing collaborators:', err);
      }
    }

    try {
      // Delete/revoke the share link
      await revokeShoppingListShare(token);
      await loadActiveShares(true);
      if (shareUrl.includes(token)) {
        setShareUrl('');
      }

      // Show success message for expired link deletion
      if (isExpired) {
        setSuccessMessage(t('share.expiredLinkDeleted') || 'Abgelaufener Link gelöscht');
        setTimeout(() => setSuccessMessage(null), 2000);
      }
    } catch (err) {
      logger.error('Error revoking share:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2><FaShareAlt /> {t('share.title') || 'Liste teilen'}</h2>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="share-modal-content">
          <div className="share-tab-content">
            {!isOwner ? (
              <p className="info-message">
                {t('share.onlyOwnerCanShare') || 'Nur der Besitzer kann diese Liste teilen.'}
              </p>
            ) : (
              <>
                <p className="share-description">
                  {shareUrl && activeShares.length > 0
                    ? (t('share.existingLinkDescription') || 'Du hast bereits einen aktiven Share-Link. Teile ihn mit anderen.')
                    : (t('share.description') || 'Erstelle einen Link und teile ihn. Jeder mit dem Link kann die Liste bearbeiten.')
                  }
                </p>

                {isLoading ? (
                  <div className="loading-state">
                    <span className="loading-spinner"></span>
                  </div>
                ) : !shareUrl ? (
                  <button
                    className="create-share-button"
                    onClick={handleCreateShare}
                    disabled={isCreatingShare}
                  >
                    {isCreatingShare ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      <>
                        <FaLink /> {t('share.createLink') || 'Share-Link erstellen'}
                      </>
                    )}
                  </button>
                ) : (
                  <div className="share-link-section">
                    <div className="share-link-input-wrapper">
                      <input
                        type="text"
                        value={shareUrl}
                        readOnly
                        className="share-link-input"
                      />
                      <button
                        className={`copy-button ${copied ? 'copied' : ''}`}
                        onClick={handleCopyLink}
                      >
                        <FaCopy />
                        {copied
                          ? (t('share.copied') || 'Kopiert!')
                          : (t('share.copy') || 'Kopieren')
                        }
                      </button>
                    </div>

                    {navigator.share && (
                      <button
                        className="native-share-button"
                        onClick={handleNativeShare}
                      >
                        <FaShareAlt /> {t('share.shareVia') || 'Teilen via...'}
                      </button>
                    )}

                    <p className="share-info">
                      {t('share.expiresIn24h') || 'Link ist 24 Stunden gültig'}
                    </p>
                  </div>
                )}

                {error && <p className="error-message">{error}</p>}
                {successMessage && <p className="success-message">{successMessage}</p>}

                {/* Show collaborators section */}
                {currentList?.collaborators?.length > 0 && (
                  <div className="collaborators-section">
                    <button
                      className="collaborators-toggle"
                      onClick={() => setShowCollaboratorsList(!showCollaboratorsList)}
                    >
                      <FaUsers />
                      <span>
                        {currentList.collaborators.length} {t('share.collaboratorsActive') || 'Mitbearbeiter haben Zugriff'}
                      </span>
                      <span className="toggle-arrow">{showCollaboratorsList ? '▲' : '▼'}</span>
                    </button>

                    {showCollaboratorsList && (
                      <ul className="collaborators-list">
                        {currentList.collaborators.map(collab => (
                          <li key={collab.id} className="collaborator-item">
                            <span className="collaborator-name">{collab.name || collab.email || 'Unbekannt'}</span>
                            {collab.email && collab.name && (
                              <span className="collaborator-email">{collab.email}</span>
                            )}
                            <button
                              className="remove-collaborator-btn"
                              onClick={() => {
                                if (window.confirm(t('share.removeCollaboratorConfirm') || `${collab.name || collab.email} wirklich entfernen?`)) {
                                  removeCollaborator(listId, collab.id);
                                }
                              }}
                              title={t('share.removeCollaborator') || 'Entfernen'}
                            >
                              <FaTrash />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Button to invite more people (create additional share link) */}
                {shareUrl && activeShares.length > 0 && (
                  <button
                    className="invite-more-button"
                    onClick={handleCreateShare}
                    disabled={isCreatingShare}
                  >
                    {isCreatingShare ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      <>
                        <FaLink /> {t('share.inviteMore') || 'Weitere Personen einladen'}
                      </>
                    )}
                  </button>
                )}

                {activeShares.length > 0 && (
                  <div className="active-shares-section">
                    <h3>{t('share.manageLinks') || 'Link-Verwaltung'}</h3>
                    <ul className="active-shares-list">
                      {activeShares.map(share => {
                        const expired = isShareExpired(share);
                        return (
                          <li key={share.id} className={`share-item ${expired ? 'share-expired' : ''}`}>
                            <div className="share-status-indicator">
                              <span
                                className={`status-dot ${expired ? 'status-expired' : 'status-active'}`}
                                title={expired
                                  ? (t('share.linkExpired') || 'Abgelaufen')
                                  : (t('share.linkActive') || 'Aktiv')
                                }
                              />
                            </div>
                            <div className="share-item-info">
                              <span className="share-token">
                                ...{share.shareToken.slice(-8)}
                              </span>
                              <span className="share-uses">
                                {share.currentUses} {t('share.usesViaLink') || 'Beitritte via Link'}
                                {expired && <span className="expired-label"> • {t('share.expired') || 'Abgelaufen'}</span>}
                              </span>
                            </div>
                            <button
                              className="revoke-button"
                              onClick={() => handleRevokeShare(share.shareToken, expired)}
                              title={expired
                                ? (t('share.deleteLink') || 'Link löschen')
                                : (t('share.revoke') || 'Widerrufen')
                              }
                            >
                              <FaTrash />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppingListShareModal;
